import { Box, Grid, Link, Typography, useMediaQuery } from '@mui/material'
import { FileList, Icon } from 'components'
import { RichTextEditor } from 'components/richtTextEditor'
import { theme } from 'config'
import { observer } from 'mobx-react'
import { Step, Video } from 'models'
import React from 'react'
import { useTranslation } from 'react-i18next'
import ReactPlayer from 'react-player'
import styled, { css } from 'styled-components'

const secondsToText = (seconds: number) => {
  return new Date(seconds * 1000).toISOString().substr(14, 5)
}

export interface IVideoCourseProps {
  step:     Step
  preview?: boolean
}

export const VideoCourse = observer((props: IVideoCourseProps) => {
  const { step, preview } = props
  // const isNotMobileView = useMediaQuery(theme.breakpoints.up('sm'))
  const { t } = useTranslation()

  React.useEffect(() => {
    if (step.video_id) step.loadVideo()
  }, [step]);

  return (
    <>
      <Grid container maxWidth='100rem' margin='auto'>
        <Grid xs={12} item>
          <Typography
            textAlign    = 'center'
            variant      = 'h4'
            marginBottom = {theme.spacing(3)}
          >
            {step.title}
          </Typography>
        </Grid>
        <Grid xs={12} item>
          <VideoPlayer video={step.video} editable={false} />
        </Grid>
      </Grid>
      <Grid container maxWidth='100rem' margin='auto'>
        <Grid display='flex' justifyContent='center' alignItems='center' mt={4} item xs={12}>
          <StyledRichTextEditor
            disable
            hideToolbar
            transparent
            readOnly = {true}
            value    = {step.getBlocks.join('')}
          />
        </Grid>
        {step.getDocuments.length > 0 && (
          <Grid mt={2} item xs={12}>
            <FileList files={step.getDocuments} />
          </Grid>
        )}
      </Grid>
    </>
  )
})

export interface IVideoPlayerProps {
  video: Video
  editable: boolean
}

export const VideoPlayer: React.FC<IVideoPlayerProps> = observer(({ video, editable }) => {
  const [second,  setSecond]  = React.useState<number>(0);
  const [playing, setPlaying] = React.useState<boolean>(false);
  const isNotMobileView = useMediaQuery(theme.breakpoints.up('sm'));

  const playerRef = React.useRef<any>(null);

  const addTracks = () => {
    // console.log('Adding tracks to video player');
    // Find the <div> with the specific ID
    const divElement = document.getElementById(video.id+'-video-player');

    if (!divElement) {
      console.error('No div with the ID '+video.id+'-video-player'+' found.');
      return;
    }

    // Look for the <video> element within that div
    const videoElement = divElement.querySelector('video');

    if (!videoElement || !video?.subtitles || !video?.subtitles.length) {
      // console.error('No <video> element found inside the div.');
      return;
    }

    // console.log({ videoElement });

    // Now, add the subtitle tracks to the <video> element
    video?.subtitles?.forEach((track) => {
      const trackElement = document.createElement('track');
      trackElement.src = track.link;
      trackElement.kind = 'subtitles';
      trackElement.srclang = track.language;
      trackElement.label = track.language.toUpperCase();
      trackElement.default = true; // Optional, to make one track the default

      // console.log('Adding track', trackElement);

      // Append the <track> to the video element if not already added
      if (!videoElement.querySelector(`track[src="${track.link}"]`)) {
        videoElement.appendChild(trackElement);
      }
    });
  };

  React.useEffect(() => {
    

    // Ensure we have the video loaded before adding tracks
    if (playerRef.current && video?.subtitles && playerRef.current.getInternalPlayer()) {
      // Add tracks once the video is loaded
      playerRef.current.getInternalPlayer().addEventListener('loadeddata', addTracks);
    }

    return () => {
      // Clean up the event listener
      if (playerRef.current && playerRef.current.getInternalPlayer()) {
        // console.log('cleeaing up event listener');
        playerRef.current.getInternalPlayer().removeEventListener('loadeddata', addTracks);
      }
    };
  }, [video, playerRef?.current]);

  const { t } = useTranslation();

  const goToSecond = (secondToPlay: number) => {
    if (playerRef.current) {
      playerRef.current.player.seekTo(secondToPlay)
      setSecond(secondToPlay)
    }
  }

  React.useEffect(() => {
    if (video) video.getVimeoVideo()
  }, [video]);

  if (!video) return null;
  // console.log(video)


  const tracks = video.subtitles.map((sub) => {
    return {label: 'English', kind: 'subtitles', src: sub.link, srcLang: 'en', default: true}
  });

  // // console.log(tracks)
  
  if(!video.url && !editable) return null;

  return (
    <Box display='flex'>
      <Box
        width={isNotMobileView ? '640px' : '360px'}
        height={isNotMobileView ? '360px' : '260px'}
        sx={{
          background: isNotMobileView ? 'black' : 'black',
          margin: '0 auto',
        }}
      >
        {video.url && (
          <PlayerStyles>
            <ReactPlayer
              key={video.url}
              id={video.id + '-video-player'}
              ref={playerRef}
              width={!isNotMobileView ? 360 : 640}
              height={isNotMobileView ? 360 : 260}
              controls={true}
              url={video.url}
              onError={(err) => {
                setPlaying(false)
                console.log(err)
              }}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              onSeek={(seconds) => setSecond(seconds)}
              onProgress={(progress) =>
                setSecond(Math.floor(progress.playedSeconds))
              }
              onReady={() => addTracks()}
              config={{
                file: {
                  tracks,
                  attributes: {
                    onContextMenu: (e: any) => e.preventDefault(),
                    controlsList: 'nodownload',
                  },
                },
              }}
            />
          </PlayerStyles>
        )}
        {!video.getVimeoId && (
          <Typography
            textAlign='center'
            color='white'
            variant='h4'
            marginBottom={theme.spacing(3)}
          >
            {t('no_vimeo_id_to_show_in_video_player')}
          </Typography>
        )}
      </Box>
      {(video.getChapters.length > 0 || editable) && (
        <ChapterList
          flexDirection={'column'}
          p={3}
          display={isNotMobileView ? 'flex' : 'none'}
        >
          {video.getChapters
            .sort((a, b) => a.second_from - b.second_from)
            .map((chapter, i) => (
              <ChapterListItem
                key={chapter.second_from}
                data-second={chapter.second_from}
                $editable={editable}
                passed={second >= chapter.second_to}
                current={
                  second >= chapter.second_from &&
                  second <= chapter.second_to &&
                  playing
                }
              >
                <ChapterLinkInner>
                  <ChapterLink onClick={() => goToSecond(chapter.second_from)}>
                    {chapter.title}
                  </ChapterLink>
                  <ChapterLinkTimeContainer>
                    <ChapterLinkTime>
                      {secondsToText(chapter.second_from)}
                    </ChapterLinkTime>
                    {chapter.second_to && (
                      <ChapterLinkTime>
                        {' '}
                        - {secondsToText(chapter.second_to)}
                      </ChapterLinkTime>
                    )}
                  </ChapterLinkTimeContainer>
                </ChapterLinkInner>
                {editable && (
                  <RemoveIcon
                    iconId='remove'
                    size='size-18'
                    onClick={() => video.removeChapter(chapter)}
                  />
                )}
              </ChapterListItem>
            ))}
        </ChapterList>
      )}
    </Box>
  )
});

const StyledRichTextEditor = styled(RichTextEditor)``;

const ChapterList = styled(Box)`
  flex:             1;
  background-color: ${theme.palette.blue.main};
  max-height:       360px;
  overflow:         auto;
`

const ChapterLink = styled(Link)`
  text-decoration: none;
  cursor:          pointer;
  font-size:       12px;
  font-weight:     600;
  color:           ${theme.palette.white.main};
`
const ChapterLinkInner = styled.div`
  flex-direction: column;
  display:        flex;
`
const ChapterLinkTimeContainer = styled.div`
  flex-direction: row;
  display:        flex;
  width:          100%;
`
const ChapterLinkTime = styled.span`
  color:       ${theme.palette.white.main};
  font-size:   10px;
  font-weight: 500;
`

const ChapterListItem = styled('div')<{
  $editable : boolean
  current?:   boolean
  passed?:    boolean
}>`
  position:        relative;
  padding-left:    3rem;
  margin-bottom:   .5rem;
  display:         flex;
  flex-direction:  row;
  justify-content: space-between;
  align-items:     center;
  &::before {
    position:         absolute;
    left:             0;
    top:              10px;
    content:          '';
    display:          inline-block;
    width:            10px;
    height:           10px;
    border-radius:    4.5px;
    animation:        ${(props) => (props.current ? 'heartbeat 2s infinite' : '')};
    background-color: ${(props) =>
      props.current
        ? theme.palette.lightGreen.main
        : props.passed
        ? theme.palette.primary.main
        : theme.palette.background.default};
  }

  ${(p) =>
    p.$editable &&
    css`
      padding-right: 2rem;
    `}
  @keyframes heartbeat {
    0% {
      transform: scale(0.75);
    }

    20% {
      transform: scale(1);
    }

    40% {
      transform: scale(0.75);
    }

    60% {
      transform: scale(1);
    }

    80% {
      transform: scale(0.75);
    }

    100% {
      transform: scale(0.75);
    }
  }
`

const RemoveIcon = styled(Icon)`
  position: relative;
  right:    0;
  top:      0;
  cursor:   pointer;

  svg {
    color: white;
  }
`

const PlayerStyles = styled.div`
  > .vp-sidedock {
    font-size: 0!important;
  }
` 